import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CartItem, Product } from '../types';

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  total: number;
  addItem: (product: Product, quantity?: number, selectedAttrs?: { color?: string; size?: string; weight?: string; variant_id?: number; variant_sku?: string }) => void;
  removeItem: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearCart: () => void;
  setCart: (items: CartItem[]) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  // Initialize state from localStorage
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Failed to load cart from localStorage:', error);
      return [];
    }
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  // Calculate total items
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // Calculate total price
  const total = items.reduce((sum, item) => {
    const price = item.product.final_price || item.product.price;
    return sum + price * item.quantity;
  }, 0);

  // Add item to cart
  const addItem = (product: Product, quantity: number = 1, selectedAttrs?: { color?: string; size?: string; weight?: string; variant_id?: number; variant_sku?: string }) => {
    setItems((prevItems) => {
      const existingItem = prevItems.find((item) =>
        item.product.id === product.id &&
        (item.selected_variant_id || 0) === (selectedAttrs?.variant_id || 0) &&
        (item.selected_color || '') === (selectedAttrs?.color || '') &&
        (item.selected_size || '') === (selectedAttrs?.size || '')
      );
      
      if (existingItem) {
        // Update quantity if item already exists
        return prevItems.map((item) =>
          item === existingItem
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new item with optional selected attributes
        return [...prevItems, {
          product,
          quantity,
          selected_color: selectedAttrs?.color || '',
          selected_size: selectedAttrs?.size || '',
          selected_weight: selectedAttrs?.weight || '',
          selected_variant_id: selectedAttrs?.variant_id,
          selected_variant_sku: selectedAttrs?.variant_sku || '',
        }];
      }
    });
  };

  // Remove item from cart
  const removeItem = (productId: number) => {
    setItems((prevItems) => prevItems.filter((item) => item.product.id !== productId));
  };

  // Update item quantity
  const updateQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }

    setItems((prevItems) =>
      prevItems.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  };

  // Clear cart
  const clearCart = () => {
    setItems([]);
  };

  // Set entire cart (replace all items)
  const setCart = (newItems: CartItem[]) => {
    setItems(newItems);
  };

  const value: CartContextType = {
    items,
    itemCount,
    total,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    setCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
